import React, { useCallback } from 'react';
import { Button, Card, Heading, Link, Strong, Text } from 'components/core';
import { Inline, Stack } from 'components/layout';

type Stage = 'select' | 'screen' | 'extract';
export type Category =
  | 'select'
  | 'screen'
  | 'extract'
  | 'irrelevant'
  | 'excluded'
  | 'included';

// Server decided empty lists, shown in place of the study list
export type EmptyListType = 'noImports' | 'importInProgress';

const STAGE_CATEGORIES: Record<Category, Stage> = {
  // Title and Screening
  screen: 'screen',
  irrelevant: 'screen',
  // Full Text Review
  select: 'select',
  excluded: 'select',
  // Extraction DE1
  extract: 'extract',
  included: 'extract',
} as const;

const STAGE_TITLES: Record<Stage, { title: string; subtitle: string }> = {
  screen: { title: 'screen', subtitle: 'screening' },
  select: { title: 'review', subtitle: 'full text review' },
  extract: { title: 'extract', subtitle: 'extracting' },
} as const;

export function EmptyListNoImports({
  review,
  category,
}: {
  review: { id: string };
  category: Category;
}) {
  const importPath = `/reviews/${review.id}/citation_imports/new?study_category=${category}`;
  const stage = STAGE_TITLES[STAGE_CATEGORIES[category]];

  return (
    <Card>
      <Stack spacing={4} alignX="center">
        <Text size="md">No studies to {stage?.title}</Text>
        <Text size="sm" variant="weak">
          Import references to start {stage?.subtitle}
        </Text>
        <Inline spacing={2}>
          <Button type="brand" variant="solid" size="sm" to={importPath}>
            Import references
          </Button>
        </Inline>
      </Stack>
    </Card>
  );
}

export function EmptyListNoStudiesFoundInStage({
  category,
}: {
  category: Category;
}) {
  const stage = STAGE_TITLES[STAGE_CATEGORIES[category]];

  return (
    <Card>
      <Stack spacing={4} alignX="center">
        <Text size="sm" variant="weak">
          No studies to {stage?.title}
        </Text>
      </Stack>
    </Card>
  );
}

export function EmptyListNoStudiesFoundGeneric({
  ...props
}: React.HTMLProps<HTMLDivElement>) {
  return (
    <Card {...props}>
      <Stack spacing={4} alignX="center">
        <Text size="sm" variant="weak">
          No studies found
        </Text>
      </Stack>
    </Card>
  );
}

export type EmptyListTrialLimitProps = {
  totalScreened: number;
  remainingStudies: number;
  isReviewOwner: boolean;
  reviewOwnerName?: string;
  fullTextReviewPath: string;
  focusOnMount?: boolean;
};

const TRIAL_HEADING_ID = 'trial-limit-heading';

export function EmptyListTrialLimit({
  totalScreened,
  remainingStudies,
  isReviewOwner,
  reviewOwnerName,
  fullTextReviewPath,
  focusOnMount = false,
}: EmptyListTrialLimitProps) {
  const focusRef = useCallback(
    (node: HTMLDivElement | null) => node?.focus(),
    []
  );

  return (
    <div
      ref={focusOnMount ? focusRef : undefined}
      role="region"
      aria-labelledby={TRIAL_HEADING_ID}
      tabIndex={focusOnMount ? -1 : undefined}
    >
      <Card>
        <Stack spacing={5} alignX="center">
          <Heading type="h4" component="h3" id={TRIAL_HEADING_ID}>
            Great work, you&apos;ve screened {totalScreened} studies!
          </Heading>

          <Stack spacing={2} alignX="center">
            <Text size="md">
              Did you get a good flow going? Don&apos;t stop now, screen the
              remaining <Strong>{remainingStudies}</Strong>.
            </Text>
            {isReviewOwner ? (
              <Text size="md">
                Upgrade your account to a paid plan and you can keep going.
              </Text>
            ) : (
              <Text size="md">
                Ask the review owner, {reviewOwnerName}, to upgrade to a paid
                plan in order to keep going.
              </Text>
            )}
          </Stack>

          {isReviewOwner && (
            <Button type="success" variant="solid" size="sm" to="/plans">
              Upgrade & keep going
            </Button>
          )}

          <Text size="sm" variant="weak">
            Want to keep trialling Covidence? Proceed to{' '}
            <Link to={fullTextReviewPath} variant="primary">
              full text review
            </Link>
            .
          </Text>
        </Stack>
      </Card>
    </div>
  );
}
